import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import { Trophy, Medal, Award, ChevronDown, ChevronUp, Info, ArrowUpDown, Grid3x3, BarChart3 } from 'lucide-react';
import { useTournament } from '@/context/TournamentContext';

import { API_BASE_URL } from '@/config/api';
const API = API_BASE_URL;

export default function Leaderboard() {
  const [user, setUser] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [userPicks, setUserPicks] = useState({});
  const [teams, setTeams] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'rank', direction: 'asc' });
  const [activeTab, setActiveTab] = useState('standings');
  const [distribution, setDistribution] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState({});
  const { currentTournament, selectedTournamentId } = useTournament();

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedTournamentId) return;

      try {
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(userRes.data);

        const leaderboardRes = await axios.get(
          `${API}/leaderboard?tournament_id=${selectedTournamentId}`
        );
        setLeaderboard(leaderboardRes.data);

        // Fetch teams to get elimination status
        const teamsRes = await axios.get(
          `${API}/tournaments/${selectedTournamentId}/teams`
        );
        setTeams(teamsRes.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedTournamentId]);

  const fetchDistribution = async () => {
    if (distribution) return; // already loaded
    try {
      const res = await axios.get(
        `${API}/pick-distribution?tournament_id=${selectedTournamentId}`,
        { withCredentials: true }
      );
      setDistribution(res.data);
    } catch (error) {
      console.error('Error fetching distribution:', error);
    }
  };

  const toggleTeam = (teamId) => {
    setExpandedTeams(prev => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  const toggleUser = async (userId) => {
    if (expandedUsers[userId]) {
      // Collapse
      setExpandedUsers({ ...expandedUsers, [userId]: false });
    } else {
      // Expand and fetch picks if not already loaded
      if (!userPicks[userId]) {
        try {
          const picksRes = await axios.get(
            `${API}/picks/user/${userId}?tournament_id=${selectedTournamentId}`
          );
          setUserPicks({ ...userPicks, [userId]: picksRes.data });
        } catch (error) {
          console.error('Error fetching picks:', error);
        }
      }
      setExpandedUsers({ ...expandedUsers, [userId]: true });
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedLeaderboard = () => {
    const sorted = [...leaderboard];
    
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.key === 'rank') {
        aValue = leaderboard.indexOf(a);
        bValue = leaderboard.indexOf(b);
      } else if (sortConfig.key.startsWith('round_')) {
        aValue = a.rounds_breakdown?.[sortConfig.key] || 0;
        bValue = b.rounds_breakdown?.[sortConfig.key] || 0;
      } else if (sortConfig.key === 'total') {
        aValue = a.total_points;
        bValue = b.total_points;
      } else if (sortConfig.key === 'teams_left') {
        aValue = a.teams_left || 0;
        bValue = b.teams_left || 0;
      } else if (sortConfig.key === 'max') {
        aValue = a.max_possible_points || 0;
        bValue = b.max_possible_points || 0;
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return sorted;
  };

  const sortedLeaderboard = getSortedLeaderboard();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation user={user} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-emerald-900 rounded-md p-8 mb-8 card-shadow text-white">
          <div className="flex items-center space-x-3">
            <Trophy size={48} className="text-amber-600" />
            <div>
              <h1 className="font-bebas text-5xl tracking-wider">Leaderboard</h1>
              <p className="font-manrope text-emerald-100">
                {currentTournament?.name || `${currentTournament?.year} Tournament`} Standings
              </p>
            </div>
          </div>
        </div>

        {/* Tab Toggle */}
        <div className="flex gap-2 mb-4" data-testid="leaderboard-tabs">
          <button
            onClick={() => setActiveTab('standings')}
            data-testid="tab-standings"
            className={`px-6 py-2 rounded-md font-bebas text-lg tracking-wider transition-colors ${
              activeTab === 'standings' 
                ? 'bg-emerald-900 text-white' 
                : 'bg-white text-emerald-900 border border-emerald-900 hover:bg-emerald-50'
            }`}
          >
            Standings
          </button>
          <button
            onClick={() => { setActiveTab('distribution'); fetchDistribution(); }}
            data-testid="tab-distribution"
            className={`px-6 py-2 rounded-md font-bebas text-lg tracking-wider transition-colors flex items-center gap-2 ${
              activeTab === 'distribution' 
                ? 'bg-emerald-900 text-white' 
                : 'bg-white text-emerald-900 border border-emerald-900 hover:bg-emerald-50'
            }`}
          >
            <BarChart3 size={18} />
            Pick Distribution
          </button>
        </div>

        {activeTab === 'distribution' && distribution && (
          <div className="bg-white rounded-md card-shadow">
            <div className="p-4 border-b border-gray-200">
              <p className="font-manrope text-gray-600 text-sm">{distribution.total_players} players in the pool</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-emerald-900 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left font-bebas text-lg tracking-wider">Seed</th>
                    <th className="px-4 py-3 text-left font-bebas text-lg tracking-wider">Team</th>
                    <th className="px-4 py-3 text-left font-bebas text-lg tracking-wider">Region</th>
                    <th className="px-4 py-3 text-center font-bebas text-lg tracking-wider">Picked</th>
                    <th className="px-4 py-3 text-center font-bebas text-lg tracking-wider">%</th>
                  </tr>
                </thead>
                <tbody>
                  {distribution.distribution.map((team) => {
                    const isExpanded = expandedTeams[team.team_id];
                    const pct = distribution.total_players > 0 
                      ? Math.round((team.pick_count / distribution.total_players) * 100) 
                      : 0;
                    return (
                      <React.Fragment key={team.team_id}>
                        <tr 
                          onClick={() => team.pick_count > 0 && toggleTeam(team.team_id)}
                          className={`border-b border-gray-100 transition-colors ${
                            team.eliminated ? 'opacity-50' : ''
                          } ${team.pick_count > 0 ? 'cursor-pointer hover:bg-emerald-50' : ''}`}
                          data-testid={`dist-row-${team.team_id}`}
                        >
                          <td className="px-4 py-3 font-bebas text-lg text-gray-600">{team.seed}</td>
                          <td className="px-4 py-3 font-manrope font-medium">
                            <span className={team.eliminated ? 'line-through text-red-400' : 'text-emerald-900'}>
                              {team.team_name}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-manrope text-sm text-gray-500">{team.region}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-bebas text-xl ${team.pick_count > 0 ? 'text-emerald-700' : 'text-gray-300'}`}>
                              {team.pick_count}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-emerald-600 rounded-full" 
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="font-manrope text-sm text-gray-500">{pct}%</span>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && team.pick_count > 0 && (
                          <tr className="bg-emerald-50">
                            <td colSpan="5" className="px-8 py-3">
                              <div className="flex flex-wrap gap-2">
                                {team.picked_by.map((picker) => (
                                  <Link
                                    key={picker.user_id}
                                    to={`/my-bracket?user_id=${picker.user_id}`}
                                    className="inline-block px-3 py-1 bg-white rounded-md border border-emerald-200 font-manrope text-sm text-emerald-800 hover:bg-emerald-100 transition-colors"
                                  >
                                    {picker.name}
                                  </Link>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'standings' && (
        <div className="bg-white rounded-md card-shadow">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-emerald-900 text-white">
                <tr>
                  <th 
                    onClick={() => handleSort('rank')}
                    className="px-6 py-4 text-left font-bebas text-xl tracking-wider cursor-pointer hover:bg-emerald-800 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Rank
                      <ArrowUpDown size={16} />
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left font-bebas text-xl tracking-wider">Player</th>
                  <th 
                    onClick={() => handleSort('round_1')}
                    className="px-6 py-4 text-center font-bebas text-xl tracking-wider cursor-pointer hover:bg-emerald-800 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-2">
                      R1
                      <ArrowUpDown size={16} />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('round_2')}
                    className="px-6 py-4 text-center font-bebas text-xl tracking-wider cursor-pointer hover:bg-emerald-800 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-2">
                      R2
                      <ArrowUpDown size={16} />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('round_3')}
                    className="px-6 py-4 text-center font-bebas text-xl tracking-wider cursor-pointer hover:bg-emerald-800 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-2">
                      R3
                      <ArrowUpDown size={16} />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('round_4')}
                    className="px-6 py-4 text-center font-bebas text-xl tracking-wider cursor-pointer hover:bg-emerald-800 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-2">
                      R4
                      <ArrowUpDown size={16} />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('round_5')}
                    className="px-6 py-4 text-center font-bebas text-xl tracking-wider cursor-pointer hover:bg-emerald-800 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-2">
                      F4
                      <ArrowUpDown size={16} />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('round_6')}
                    className="px-6 py-4 text-center font-bebas text-xl tracking-wider cursor-pointer hover:bg-emerald-800 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-2">
                      Champ
                      <ArrowUpDown size={16} />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('total')}
                    className="px-6 py-4 text-center font-bebas text-xl tracking-wider cursor-pointer hover:bg-emerald-800 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-2">
                      Total
                      <ArrowUpDown size={16} />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('teams_left')}
                    className="px-6 py-4 text-center font-bebas text-xl tracking-wider cursor-pointer hover:bg-emerald-800 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-2">
                      Alive
                      <ArrowUpDown size={16} />
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('max')}
                    className="px-6 py-4 text-center font-bebas text-xl tracking-wider cursor-pointer hover:bg-emerald-800 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-1">
                      Max
                      <ArrowUpDown size={16} />
                      <div className="relative group">
                        <Info size={14} className="text-gray-400 cursor-help" />
                        <div className="absolute right-0 top-full mt-2 hidden group-hover:block w-64 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-50 whitespace-normal text-center">
                          <strong>Beta Feature</strong> - Max possible score may not be accurate. Available after Round 1 completes.
                        </div>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedLeaderboard.map((entry, index) => {
                  const isCurrentUser = entry.user_id === user?.user_id;
                  const isExpanded = expandedUsers[entry.user_id];
                  const picks = userPicks[entry.user_id] || [];
                  const originalRank = leaderboard.indexOf(entry) + 1;
                  
                  return (
                    <React.Fragment key={entry.user_id}>
                      <tr
                        data-testid={`leaderboard-row-${index}`}
                        onClick={() => toggleUser(entry.user_id)}
                        className={`border-b border-gray-200 cursor-pointer transition-colors ${
                          isCurrentUser ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-100'
                        } ${
                          originalRank === 1
                            ? 'bg-gradient-to-r from-amber-100 to-yellow-50'
                            : originalRank === 2
                            ? 'bg-gradient-to-r from-gray-100 to-gray-50'
                            : originalRank === 3
                            ? 'bg-gradient-to-r from-orange-100 to-orange-50'
                            : ''
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            {isExpanded ? <ChevronUp size={20} className="text-emerald-900" /> : <ChevronDown size={20} className="text-emerald-900" />}
                            {originalRank === 1 && <Trophy className="text-amber-600" size={24} />}
                            {originalRank === 2 && <Medal className="text-gray-500" size={24} />}
                            {originalRank === 3 && <Award className="text-orange-600" size={24} />}
                            <span className="font-bebas text-2xl text-emerald-900">{originalRank}</span>
                          </div>
                        </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          {entry.picture && (
                            <img
                              src={entry.picture}
                              alt={entry.name}
                              className="w-10 h-10 rounded-full"
                            />
                          )}
                          <div>
                            <div className="font-manrope font-bold text-emerald-900">
                              {entry.name}
                              {isCurrentUser && (
                                <span className="ml-2 text-xs bg-amber-600 text-white px-2 py-1 rounded">
                                  YOU
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-manrope text-gray-700">
                          {entry.rounds_breakdown?.round_1 || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-manrope text-gray-700">
                          {entry.rounds_breakdown?.round_2 || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-manrope text-gray-700">
                          {entry.rounds_breakdown?.round_3 || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-manrope text-gray-700">
                          {entry.rounds_breakdown?.round_4 || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-manrope text-gray-700">
                          {entry.rounds_breakdown?.round_5 || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-manrope text-gray-700">
                          {entry.rounds_breakdown?.round_6 || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-bebas text-3xl text-emerald-900">
                          {entry.total_points}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-bebas text-2xl text-emerald-700">
                          {entry.teams_left ?? 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-bebas text-2xl text-gray-600">
                          {entry.max_possible_points != null ? entry.max_possible_points : '—'}
                        </span>
                      </td>
                    </tr>
                    
                    {/* Expanded Picks Row */}
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan="11" className="px-6 py-4">
                          <div className="font-bebas text-xl text-emerald-900 mb-3">
                            {entry.name}'s Picks
                          </div>
                          {picks.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-gray-300">
                                    <th className="py-2 px-3 text-left font-bebas text-base tracking-wider text-emerald-900">Seed</th>
                                    <th className="py-2 px-3 text-left font-bebas text-base tracking-wider text-emerald-900">Team</th>
                                    <th className="py-2 px-3 text-center font-bebas text-base tracking-wider text-emerald-900">Wins</th>
                                    <th className="py-2 px-3 text-center font-bebas text-base tracking-wider text-emerald-900">Pts/Win</th>
                                    <th className="py-2 px-3 text-center font-bebas text-base tracking-wider text-emerald-900">Points</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...picks].sort((a, b) => {
                                    const teamA = teams.find(t => t.team_id === a.team_id);
                                    const teamB = teams.find(t => t.team_id === b.team_id);
                                    const elimA = teamA?.eliminated || false;
                                    const elimB = teamB?.eliminated || false;
                                    const ptsA = a.points || 0;
                                    const ptsB = b.points || 0;
                                    // Winners first (by points desc), then alive (by seed asc), then eliminated (by seed asc)
                                    if (ptsA !== ptsB) return ptsB - ptsA;
                                    if (elimA !== elimB) return elimA ? 1 : -1;
                                    return a.seed - b.seed;
                                  }).map((pick) => {
                                    const team = teams.find(t => t.team_id === pick.team_id);
                                    const isEliminated = team?.eliminated || false;
                                    const hasPoints = (pick.points || 0) > 0;
                                    return (
                                      <tr
                                        key={pick.team_id}
                                        className={`border-b border-gray-200 ${
                                          isEliminated 
                                            ? 'bg-red-50 text-red-400 line-through' 
                                            : hasPoints 
                                              ? 'bg-emerald-50' 
                                              : ''
                                        }`}
                                      >
                                        <td className="py-2 px-3 font-bebas text-lg">{pick.seed}</td>
                                        <td className="py-2 px-3 font-manrope">{pick.team_name}</td>
                                        <td className="py-2 px-3 text-center font-manrope">{pick.wins || 0}</td>
                                        <td className="py-2 px-3 text-center font-manrope text-gray-500">{pick.pts_per_win || pick.seed + 9}</td>
                                        <td className="py-2 px-3 text-center font-bebas text-lg">
                                          {hasPoints ? <span className="text-emerald-700">{pick.points}</span> : <span className="text-gray-400">0</span>}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-gray-500 font-manrope">No picks submitted yet</p>
                          )}
                          <Link
                            to={`/my-bracket?user_id=${entry.user_id}`}
                            data-testid={`view-bracket-${entry.user_id}`}
                            className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-emerald-900 text-white rounded-md font-bebas text-lg tracking-wider hover:bg-emerald-800 transition-colors"
                          >
                            <Grid3x3 size={16} />
                            View Bracket
                          </Link>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}